/**
 * Shared category facet renderer for MotoMillion and DucabikeDirect.
 * Native checkbox markup uses a guarded progressive-enhancement handoff.
 * Older tree-only markup builds detached and retains its last good render on failure.
 */
(function () {
  if (window.mmMotoTreeProgressiveRuntime) {
    if (window.mmMotoTreeInitAll) window.mmMotoTreeInitAll();
    return;
  }

  var categoryParam = 'filter.p.m.custom.category';

  function decodeHtmlEntities(value) {
    var decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    return decoder.value;
  }

  function rootsIn(scope) {
    scope = scope || document;
    var roots = Array.from(scope.querySelectorAll('[data-mm-moto-tree-root]'));
    if (scope.matches && scope.matches('[data-mm-moto-tree-root]')) roots.unshift(scope);
    return roots;
  }

  function stateFor(root) {
    if (!root.mmMotoTreeRuntimeState) {
      root.mmMotoTreeRuntimeState = {
        nativeDisabled: new Map(), nativeByUid: new Map(), customByUid: new Map(),
        cleanup: [], nativeSizing: [], enhanced: false
      };
    }
    return root.mmMotoTreeRuntimeState;
  }

  function restoreNative(root) {
    var state = stateFor(root);
    if (state.enhanced) {
      state.customByUid.forEach(function (input, uid) {
        var nativeInput = state.nativeByUid.get(uid);
        if (nativeInput) nativeInput.checked = input.checked;
      });
    }
    state.cleanup.forEach(function (cleanup) {
      try { cleanup(); } catch (error) { /* Native controls must still be restored. */ }
    });
    state.cleanup = [];
    state.nativeSizing.forEach(function (entry) {
      entry.element.style.height = entry.height;
      entry.element.style.maxHeight = entry.maxHeight;
      entry.element.style.overflow = entry.overflow;
    });
    state.nativeSizing = [];
    state.enhanced = false;
    state.customByUid.clear();
    var native = root.querySelector('[data-mm-moto-native-fallback]');
    var enhancement = root.querySelector('[data-mm-moto-enhancement]');
    if (native) {
      Array.from(native.querySelectorAll('input')).forEach(function (input) {
        if (state.nativeDisabled.has(input)) input.disabled = state.nativeDisabled.get(input);
      });
      native.hidden = false;
      native.style.display = '';
    }
    if (enhancement) {
      Array.from(enhancement.querySelectorAll('input')).forEach(function (input) { input.disabled = true; });
      enhancement.hidden = true;
      enhancement.style.display = 'none';
    }
    var mount = root.querySelector('[data-mm-moto-tree-mount]');
    if (mount) mount.innerHTML = '';
    var display = root.closest('.faceted-filter-group-display');
    var emptyMessage = display && display.querySelector('.mm-filter-search-empty');
    if (emptyMessage) emptyMessage.classList.remove('is-visible');
    root.dataset.mmMotoTreeBuilt = 'false';
    root.dataset.mmMotoTreeState = 'native';
  }

  function syncHeight(root) {
    if (root.dataset.mmMotoTreeBuilt !== 'true') return;
    var display = root.closest('.faceted-filter-group-display');
    var group = root.closest('.faceted-filter-group');
    if (!display || !group || !group.open) return;
    var state = stateFor(root);
    if (!state.nativeSizing.length) {
      state.nativeSizing = [group, display].map(function (element) {
        return { element: element, height: element.style.height || '',
          maxHeight: element.style.maxHeight || '', overflow: element.style.overflow || '' };
      });
    }
    var summary = group.querySelector(':scope > summary');
    display.style.height = 'auto';
    display.style.maxHeight = 'none';
    display.style.overflow = 'hidden';
    var height = (summary ? summary.offsetHeight : 0) + display.scrollHeight;
    group.style.height = height + 'px';
    group.style.maxHeight = height + 'px';
    group.style.overflow = 'hidden';
  }

  function scheduleHeight(root) {
    requestAnimationFrame(function () { requestAnimationFrame(function () { syncHeight(root); }); });
  }

  function bindLifecycle(root, mount, state) {
    function listen(target, type, handler, capture) {
      target.addEventListener(type, handler, capture);
      state.cleanup.push(function () { target.removeEventListener(type, handler, capture); });
    }
    listen(root, 'toggle', function (event) {
      if (event.target && event.target.classList && event.target.classList.contains('mm-moto-accordion')) scheduleHeight(root);
    }, true);
    listen(root, 'click', function (event) {
      if (event.target && event.target.closest && event.target.closest('.mm-moto-accordion > summary')) scheduleHeight(root);
    }, true);
    listen(root, 'change', function (event) {
      var input = event.target;
      if (!input || !input.getAttribute || !state.enhanced) return;
      var uid = input.getAttribute('data-mm-moto-value-uid');
      var nativeInput = uid === null ? null : state.nativeByUid.get(uid);
      if (nativeInput) nativeInput.checked = input.checked;
    }, false);
    var group = root.closest('.faceted-filter-group');
    var display = root.closest('.faceted-filter-group-display');
    if (group && display) {
      listen(group, 'toggle', function () {
        if (group.open) scheduleHeight(root);
        else {
          group.style.height = ''; group.style.maxHeight = ''; group.style.overflow = '';
          display.style.height = ''; display.style.maxHeight = ''; display.style.overflow = '';
        }
      }, false);
    }
    if (window.ResizeObserver) {
      var observer = new window.ResizeObserver(function () { scheduleHeight(root); });
      state.cleanup.push(function () { observer.disconnect(); });
      observer.observe(mount);
      if (group) observer.observe(group);
    }
  }

  function validTree(tree) {
    if (!tree || !Array.isArray(tree.children) || !tree.children.length) return false;
    var ancestors = new Set();
    function visit(node, depth) {
      if (!node || typeof node.name !== 'string' || !node.name.trim() || depth > 64 || ancestors.has(node)) return false;
      if (node.children !== undefined && !Array.isArray(node.children)) return false;
      ancestors.add(node);
      var valid = (node.children || []).every(function (child) { return visit(child, depth + 1); });
      ancestors.delete(node);
      return valid;
    }
    return visit(tree, 0);
  }

  function buildTree(root, force) {
    if (!root) return false;
    if (!force && root.dataset.mmMotoTreeBuilt === 'true') return true;
    var native = root.querySelector('[data-mm-moto-native-fallback]');
    var enhancement = root.querySelector('[data-mm-moto-enhancement]');
    var liveMount = root.querySelector('[data-mm-moto-tree-mount]');
    var valuesEl = root.querySelector('[data-mm-moto-values-json]');
    var progressive = !!(native || enhancement || root.dataset.mmMotoFilterParam);
    if (!liveMount || !valuesEl) return false;
    if (progressive) {
      restoreNative(root);
      if (!native || !enhancement || root.dataset.mmMotoFilterParam !== categoryParam) return false;
    }
    var state = stateFor(root);
    try {
      var treeSource = window.MM_MOTO_TREE_DATA;
      if (!treeSource) {
        var inlineEl = root.querySelector('[data-mm-moto-tree-json]');
        if (inlineEl) treeSource = JSON.parse(inlineEl.textContent);
      }
      if (!validTree(treeSource)) return false;
      var expectedRoot = root.dataset.mmMotoExpectedRoot;
      if (expectedRoot && treeSource.name !== expectedRoot) return false;
      var values = JSON.parse(valuesEl.textContent);
      if (!Array.isArray(values)) return false;
      var nativeByUid = new Map();
      Array.from(native ? native.querySelectorAll('input') : []).forEach(function (input) {
        if (input.type !== 'checkbox') return;
        var uid = input.getAttribute('data-mm-moto-native-uid');
        if (uid === null || nativeByUid.has(uid)) throw new Error('Invalid native UID');
        nativeByUid.set(uid, input);
        if (!state.nativeDisabled.has(input)) state.nativeDisabled.set(input, input.disabled);
      });
      var valueUids = new Set();
      values.forEach(function (item) {
        if (!item || (typeof item.uid !== 'string' && typeof item.uid !== 'number') ||
            typeof item.label !== 'string' || typeof item.value !== 'string' ||
            typeof item.param_name !== 'string' || !item.param_name ||
            (progressive && item.param_name !== categoryParam) || typeof item.active !== 'boolean' ||
            !Number.isFinite(Number(item.count)) || Number(item.count) < 0) throw new Error('Invalid facet value');
        var uid = String(item.uid);
        var input = nativeByUid.get(uid);
        if (valueUids.has(uid)) throw new Error('Duplicate facet UID');
        valueUids.add(uid);
        if (progressive) {
          if (!input || input.name !== item.param_name) throw new Error('Native/value mismatch');
          if (input.value !== item.value && decodeHtmlEntities(item.value) !== input.value) throw new Error('Native/value mismatch');
          // Native DOM values preserve literal entities and Shopify's encoded JSON layer.
          item.value = input.value;
          item.active = !!input.checked;
        } else {
          // Tree-only Liquid serializes escaped values without native DOM controls.
          item.value = decodeHtmlEntities(item.value);
          var previous = state.customByUid.get(uid);
          if (previous && previous.name === item.param_name && previous.value === item.value) item.active = !!previous.checked;
        }
      });
      if (progressive && nativeByUid.size !== values.length) throw new Error('Native/value count mismatch');
      if (progressive) state.nativeByUid = nativeByUid;
      var tree = sortMotoTree(JSON.parse(JSON.stringify(treeSource)));
      var mount = document.createElement('div');
      var showCount = root.dataset.showCount === 'true';
      var normalize = function (value) { return (value || '').toString().trim().toLowerCase(); };
      var pathToString = function (segments) { return segments.join(' > '); };
      var configuredPrimary = normalize(root.dataset.primaryBranch !== undefined ? root.dataset.primaryBranch : window.MM_MOTO_TREE_PRIMARY_BRANCH);
      var primaryTemplate = root.dataset.primaryTemplate !== undefined ? root.dataset.primaryTemplate : window.MM_MOTO_TREE_PRIMARY_TEMPLATE;
      primaryTemplate = typeof primaryTemplate === 'string' ? primaryTemplate.trim() : '';
      var primaryEnabled = !!configuredPrimary && (!primaryTemplate ||
        (!/\s/.test(primaryTemplate) && document.body && document.body.classList.contains(primaryTemplate)));
      var visualRootDepthOffset = configuredPrimary && tree.children.some(function (node) {
        return normalize(node.name) === configuredPrimary;
      }) ? 1 : 0;
      if (!root.mmMotoTreeCheckboxPrefix) {
        window.mmMotoTreeNextRootId = (Number(window.mmMotoTreeNextRootId) || 0) + 1;
        root.mmMotoTreeCheckboxPrefix = 'MM-Filter-' + window.mmMotoTreeNextRootId + '-';
      }
      var checkboxIdPrefix = root.mmMotoTreeCheckboxPrefix;
      var preferredPaths = Object.create(null);
      preferredPaths['heated gear'] = 'Motorcycles > Riding Gear > Heated Gear';
      var overrides = window.MM_MOTO_TREE_PREFERRED_PATHS;
      if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
        Object.keys(overrides).forEach(function (key) {
          var value = overrides[key];
          if (normalize(key) && typeof value === 'string' && value.trim()) preferredPaths[normalize(key)] = value.trim();
        });
      }
      var valuesByLabel = new Map();
      values.forEach(function (item) {
        item.label = decodeHtmlEntities(item.label);
        // Preserve the native submitting value established above.
        item.visible = item.active || Number(item.count) > 0;
        if (!item.visible) return;
        var key = normalize(item.label);
        if (!valuesByLabel.has(key)) valuesByLabel.set(key, []);
        valuesByLabel.get(key).push(item);
      });
      var claimed = new Set();
      function claimValueForNode(nodePath) {
        var key = normalize(nodePath[nodePath.length - 1]);
        var matches = valuesByLabel.get(key) || [];
        var preferredPath = preferredPaths[key];
        if (preferredPath) {
          var pathString = normalize(pathToString(nodePath));
          preferredPath = normalize(preferredPath);
          if (pathString !== preferredPath && pathString.slice(-(preferredPath.length + 3)) !== ' > ' + preferredPath) return null;
        }
        var item = matches.find(function (candidate) { return !claimed.has(String(candidate.uid)); });
        if (!item) return null;
        claimed.add(String(item.uid));
        return item;
      }
    function sortMotoTree(node) {
      if (!node || !Array.isArray(node.children)) return node;
      node.children = node.children
        .map(function (child) { return sortMotoTree(child); })
        .sort(function (a, b) {
          return (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
            numeric: true
          });
        });
      return node;
    }

    function buildCheckbox(item, idSuffix) {
      var li = document.createElement('li');
      li.className = 'faceted-filter-group-display__list-item mm-moto-item';

      var label = document.createElement('label');
      label.className = 'faceted-filter-group-display__list-item-label';
      label.setAttribute('for', idSuffix);

      var input = document.createElement('input');
      input.className = 'faceted-filter-group-display__list-item-input';
      input.type = 'checkbox';
      input.name = item.param_name;
      input.value = item.value;
      input.id = idSuffix;
      input.setAttribute('data-mm-moto-value-uid', String(item.uid));
      input.disabled = true;
      if (item.active) input.checked = true;

      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'faceted-filter-group-display__checkmark');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('role', 'presentation');
      svg.setAttribute('width', '14');
      svg.setAttribute('height', '14');
      svg.setAttribute('viewBox', '0 0 14 14');

      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', 'faceted-filter-group-display__checkmark-check');
      path.setAttribute('fill', 'none');
      path.setAttribute('d', 'M2.5 7L5.5 10L11.5 4');
      svg.appendChild(path);

      var text = document.createElement('span');
      text.className = 'faceted-filter-group-display__list-item-label-text';
      text.textContent = showCount ? item.label + ' (' + item.count + ')' : item.label;

      label.appendChild(input);
      label.appendChild(svg);
      label.appendChild(text);
      li.appendChild(label);

      return li;
    }

    function renderNode(node, path) {
      var nodePath = path.concat([node.name]);
      var directValue = claimValueForNode(nodePath);

      var childElements = [];
      var hasActiveDescendant = !!(directValue && directValue.active);

      if (Array.isArray(node.children) && node.children.length) {
        node.children.forEach(function (child) {
          var renderedChild = renderNode(child, nodePath);
          if (renderedChild) {
            childElements.push(renderedChild.element);
            if (renderedChild.hasActive) hasActiveDescendant = true;
          }
        });
      }

      var directVisible = !!directValue;

      if (!directVisible && childElements.length === 0) {
        return null;
      }

      if (childElements.length > 0) {
        var details = document.createElement('details');
        details.className = 'mm-moto-accordion mm-moto-accordion--level-' + Math.min(Math.max(nodePath.length - 1 - visualRootDepthOffset, 0), 5);
        if (hasActiveDescendant) details.open = true;

        var summary = document.createElement('summary');
        var summaryText = document.createElement('span');
        summaryText.textContent = node.name;
        summary.appendChild(summaryText);

        var body = document.createElement('div');
        body.className = 'mm-moto-accordion__body';

        if (directVisible) {
          body.appendChild(
            buildCheckbox(
              directValue,
              checkboxIdPrefix + directValue.uid
            )
          );
        }

        childElements.forEach(function (childElement) { body.appendChild(childElement); });

        details.appendChild(summary);
        details.appendChild(body);

        return { element: details, hasActive: hasActiveDescendant };
      }

      if (!directVisible) return null;

      return {
        element: buildCheckbox(
          directValue,
          checkboxIdPrefix + directValue.uid
        ),
        hasActive: !!directValue.active
      };
    }

    function flattenSinglePathMount() {
      mount.classList.remove('mm-moto-tree--flattened');

      var topLevelNodes = Array.from(mount.children).filter(function (element) {
        return element.classList.contains('mm-moto-accordion') || element.classList.contains('mm-moto-item');
      });

      if (topLevelNodes.length !== 1 || !topLevelNodes[0].classList.contains('mm-moto-accordion')) {
        return;
      }

      var current = topLevelNodes[0];

      while (current && current.classList.contains('mm-moto-accordion')) {
        var body = current.querySelector(':scope > .mm-moto-accordion__body');
        if (!body) break;

        var bodyChildren = Array.from(body.children).filter(function (element) {
          return element.classList.contains('mm-moto-accordion') || element.classList.contains('mm-moto-item');
        });

        var childAccordions = bodyChildren.filter(function (element) { return element.classList.contains('mm-moto-accordion'); });
        var childItems = bodyChildren.filter(function (element) { return element.classList.contains('mm-moto-item'); });

        if (childItems.length > 0 && childAccordions.length === 0) {
          mount.innerHTML = '';
          bodyChildren.forEach(function (element) { mount.appendChild(element); });
          return;
        }

        if (childItems.length === 0 && childAccordions.length === 1 && bodyChildren.length === 1) {
          current = childAccordions[0];
          continue;
        }

        if (childItems.length === 0 && childAccordions.length > 1 && bodyChildren.length === childAccordions.length) {
          mount.innerHTML = '';
          bodyChildren.forEach(function (element) { mount.appendChild(element); });
          return;
        }

        break;
      }

      if (current && current !== topLevelNodes[0]) {
        mount.innerHTML = '';
        mount.appendChild(current);
      }
    }


      // Claim in the original sorted walk; primary presentation never reallocates values.
      var renderedBranches = [];
      (tree.children || []).forEach(function (child) {
        var rendered = renderNode(child, [tree.name]);
        if (rendered) renderedBranches.push({ name: normalize(child.name), element: rendered.element });
      });
      if (primaryEnabled) {
        var primary = renderedBranches.find(function (branch) { return branch.name === configuredPrimary; });
        if (primary) {
          mount.appendChild(primary.element);
          flattenSinglePathMount();
        }
        var firstSecondary = true;
        renderedBranches.forEach(function (branch) {
          if (branch === primary) return;
          if (primary) {
            branch.element.classList.add('mm-moto-accordion--secondary');
            if (firstSecondary) branch.element.classList.add('mm-moto-accordion--secondary-start');
            firstSecondary = false;
          }
          mount.appendChild(branch.element);
        });
      } else {
        renderedBranches.forEach(function (branch) { mount.appendChild(branch.element); });
        flattenSinglePathMount();
      }
      var unclaimed = values.filter(function (item) { return item.visible && !claimed.has(String(item.uid)); });
      if (unclaimed.length) {
        unclaimed.sort(function (a, b) { return a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }); });
        var other = document.createElement('details');
        other.className = 'mm-moto-accordion mm-moto-accordion--level-1 mm-moto-accordion--other';
        var summary = document.createElement('summary');
        var text = document.createElement('span'); text.textContent = root.dataset.otherLabel || 'Other'; summary.appendChild(text);
        var body = document.createElement('div'); body.className = 'mm-moto-accordion__body';
        unclaimed.forEach(function (item) {
          claimed.add(String(item.uid));
          body.appendChild(buildCheckbox(item, checkboxIdPrefix + item.uid));
          if (item.active) other.open = true;
        });
        other.appendChild(summary); other.appendChild(body); mount.appendChild(other);
      }
      var customByUid = new Map();
      Array.from(mount.querySelectorAll('input')).forEach(function (input) {
        var uid = input.getAttribute('data-mm-moto-value-uid');
        var item = values.find(function (candidate) { return String(candidate.uid) === uid; });
        if (!item || customByUid.has(uid) || input.name !== item.param_name || input.value !== item.value || !!input.checked !== !!item.active) throw new Error('Incomplete enhanced controls');
        customByUid.set(uid, input);
      });
      if (customByUid.size !== values.filter(function (item) { return item.visible; }).length) throw new Error('Enhanced value count mismatch');
      // Commit only a complete detached render. Tree-only markup keeps its old controls until here.
      if (!progressive) {
        state.cleanup.forEach(function (cleanup) { cleanup(); });
        state.cleanup = [];
        liveMount.innerHTML = '';
      }
      while (mount.children.length) liveMount.appendChild(mount.children[0]);
      bindLifecycle(root, liveMount, state);
      state.customByUid = customByUid;
      if (progressive) {
        nativeByUid.forEach(function (input) { input.disabled = true; });
        native.hidden = true; native.style.display = 'none';
        Array.from(enhancement.querySelectorAll('input')).forEach(function (input) { input.disabled = false; });
        enhancement.hidden = false; enhancement.style.display = '';
      } else {
        customByUid.forEach(function (input) { input.disabled = false; });
      }
      state.enhanced = true;
      root.dataset.mmMotoTreeBuilt = 'true';
      root.dataset.mmMotoTreeState = 'enhanced';
      scheduleHeight(root);
      var searchScope = enhancement || root.closest('.faceted-filter-group-display') || root;
      if (window.mmSidebarFilterSearchInitAll) window.mmSidebarFilterSearchInitAll(searchScope);
      var search = searchScope.querySelector('[data-mm-filter-search-input]');
      if (search && search.value) search.dispatchEvent(new CustomEvent('input'));
      return true;
    } catch (error) {
      if (progressive) restoreNative(root);
      return false;
    }
  }

  function initAllTrees(scope) { rootsIn(scope).forEach(function (root) { buildTree(root); }); }
  function rebuildAllTrees(scope) { rootsIn(scope).forEach(function (root) { buildTree(root, true); }); }
  function syncOpenTreeHeights(scope) { rootsIn(scope).forEach(syncHeight); }
  window.mmMotoTreeBuild = buildTree;
  window.mmMotoTreeInitAll = initAllTrees;
  window.mmMotoTreeRebuildAll = rebuildAllTrees;
  window.mmMotoTreeSyncHeights = syncOpenTreeHeights;
  window.mmMotoTreeProgressiveRuntime = true;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAllTrees(); });
  else initAllTrees();
  window.addEventListener('load', function () { initAllTrees(); });
  window.addEventListener('pageshow', function () { initAllTrees(); });
  document.addEventListener('shopify:section:load', function (event) { rebuildAllTrees(event.target || document); });
  document.addEventListener('mm:filter:resize', function (event) {
    var scope = event.target || document;
    requestAnimationFrame(function () { syncOpenTreeHeights(scope); });
  });
})();
